import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  TablePagination,
  Tooltip,
  Drawer,
  useMediaQuery,
  CircularProgress,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";
import SwapVertIcon from "@material-ui/icons/SwapVert";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import * as api from "../../api";
import DateRangePicker from "./DateRangePicker";
import { ItemHero, ItemTimeline } from "./ItemJourney";
import useDebounce from "../../utils/useDebounce";
import { useSnackbar } from "../../utils/useSnackbar";
import { useTimezone } from "../../Context/TimezoneContext";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import { getCurrencySymbol } from "../../utils/currency";
import {
  KIND_META,
  KIND_ORDER,
  DOC_TYPE_LABEL,
  presetRange,
  dayStartISO,
  dayEndISO,
  fmtQty,
  fmtSigned,
  fmtMoney,
} from "./ledgerConstants";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5, 2),
    boxSizing: "border-box",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 8,
    flexShrink: 0,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    fontSize: "1.15rem",
    color: "#0f172a",
    margin: 0,
    [theme.breakpoints.down("xs")]: { fontSize: "1rem" },
  },
  titleIcon: { color: "#ef7f1a", fontSize: 24 },
  subtitle: { fontSize: "0.72rem", color: "#64748b", margin: 0 },
  panel: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: theme.spacing(1),
    marginBottom: 8,
    flexShrink: 0,
  },
  filterRow: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" },
  grow: { flex: "1 1 220px", minWidth: 160 },
  itemPicker: { flex: "1 1 240px", minWidth: 180 },
  dateField: { width: 138 },
  presetRow: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8, alignItems: "center" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 9999,
    fontSize: "0.72rem",
    fontWeight: 600,
    lineHeight: 1.5,
    cursor: "pointer",
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    userSelect: "none",
    transition: "all .12s",
    "&:hover": { borderColor: "#f97316" },
  },
  chipActive: { background: "#fff7ed", borderColor: "#f97316", color: "#c2410c" },
  chipCount: { fontWeight: 700, opacity: 0.85 },
  sep: { width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" },
  itemCard: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1, 2.5),
    alignItems: "center",
    background: "linear-gradient(135deg,#fff7ed,#fff)",
    border: "1px solid #fed7aa",
    borderRadius: 10,
    padding: theme.spacing(0.75, 1.25),
    marginBottom: 8,
    flexShrink: 0,
  },
  stat: { display: "flex", flexDirection: "column", minWidth: 70 },
  statLabel: { fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: ".04em", color: "#94a3b8", fontWeight: 700 },
  statValue: { fontSize: "1rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.2 },
  body: {
    flex: 1,
    minHeight: 0,
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    position: "relative",
  },
  scroll: { flex: 1, overflow: "auto", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: "0.78rem" },
  th: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.66rem",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    textAlign: "left",
    padding: "7px 10px",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },
  td: { padding: "6px 10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "middle", color: "#0f172a" },
  tr: { cursor: "pointer", "&:hover": { background: "#fffaf5" } },
  right: { textAlign: "right" },
  muted: { color: "#94a3b8", fontSize: "0.68rem" },
  strong: { fontWeight: 700 },
  ellipsis: { maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  kind: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 6,
    fontSize: "0.66rem",
    fontWeight: 700,
    whiteSpace: "nowrap",
    border: "1px solid transparent",
  },
  plus: { color: "#15803d", fontWeight: 800 },
  minus: { color: "#dc2626", fontWeight: 800 },
  deleted: { color: "#dc2626", fontSize: "0.62rem", fontWeight: 700, marginLeft: 4 },
  cards: { padding: theme.spacing(0.75), display: "flex", flexDirection: "column", gap: 6 },
  card: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "7px 9px",
    background: "#fff",
    cursor: "pointer",
    "&:active": { background: "#fffaf5" },
  },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  cardMid: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginTop: 3 },
  cardBot: { display: "flex", justifyContent: "space-between", gap: 8, marginTop: 3, flexWrap: "wrap" },
  overlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(255,255,255,.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  empty: { padding: theme.spacing(5, 2), textAlign: "center", color: "#64748b" },
  footer: { borderTop: "1px solid #e2e8f0", flexShrink: 0 },
  // drawer
  drawer: { width: 400, maxWidth: "100vw", padding: 0 },
  dHead: {
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    alignItems: "flex-start",
    background: "#fffaf5",
  },
  dBody: { padding: "8px 14px 20px", overflowY: "auto" },
  dSection: { marginTop: 12 },
  dTitle: { fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 800, color: "#94a3b8", marginBottom: 4 },
  dRow: { display: "flex", justifyContent: "space-between", gap: 12, padding: "4px 0", borderBottom: "1px dashed #f1f5f9", fontSize: "0.8rem" },
  dKey: { color: "#64748b" },
  dVal: { color: "#0f172a", fontWeight: 600, textAlign: "right", wordBreak: "break-word" },
}));

const Kind = ({ kind, classes }) => {
  const m = KIND_META[kind] || KIND_META.OTHER;
  return (
    <span className={classes.kind} style={{ color: m.color, background: m.bg, borderColor: m.border }}>
      {m.label}
    </span>
  );
};

const DRow = ({ k, v, classes }) =>
  v == null || v === "" ? null : (
    <div className={classes.dRow}>
      <span className={classes.dKey}>{k}</span>
      <span className={classes.dVal}>{v}</span>
    </div>
  );

const DOC_PREFIX = { invoice: "INV", purchase: "PUR", adjustment: "ADJ", return: "RET" };
const DOC_PATH = { invoice: "/invoice/", purchase: "/purchase/", adjustment: "/stockAdjustment/" };

export default function StockLedger() {
  const classes = useStyles();
  const { openSnackbar } = useSnackbar();
  const { formatInTimezone } = useTimezone();
  const profile = useEffectiveProfile();
  const symbol = getCurrencySymbol(profile?.currency);
  const location = useLocation();
  // The SuperAdmin shell has no invoice/purchase pages to link into.
  const inSuperAdmin = location.pathname.startsWith("/superadmin");
  const isMobile = useMediaQuery("(max-width:700px)");

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);
  const [item, setItem] = useState(null);
  const [itemQuery, setItemQuery] = useState("");
  const debouncedItemQuery = useDebounce(itemQuery, 250);
  const [itemOptions, setItemOptions] = useState([]);
  const [itemLoading, setItemLoading] = useState(false);
  const [preset, setPreset] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kinds, setKinds] = useState([]);
  const [sort, setSort] = useState("desc");
  const [view, setView] = useState("timeline");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);

  const [data, setData] = useState({ rows: [], total: 0, kindCounts: {}, item: null });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const reqId = useRef(0);

  // Item picker: server-side search + infinite scroll (30 per page).
  const itemPage = useRef(1);
  const [itemHasMore, setItemHasMore] = useState(false);
  const fetchItems = useCallback((q, pageNo) => {
    setItemLoading(true);
    return api
      .searchStockLedgerItems(q, pageNo)
      .then(({ data: d }) => {
        setItemOptions((prev) => (pageNo === 1 ? d.items || [] : [...prev, ...(d.items || [])]));
        setItemHasMore(!!d.hasMore);
        itemPage.current = pageNo;
      })
      .catch(() => {})
      .finally(() => setItemLoading(false));
  }, []);
  useEffect(() => {
    fetchItems(debouncedItemQuery, 1);
  }, [debouncedItemQuery, fetchItems]);
  const onItemListScroll = (e) => {
    const el = e.currentTarget;
    if (itemHasMore && !itemLoading && el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
      fetchItems(debouncedItemQuery, itemPage.current + 1);
    }
  };

  const params = useMemo(
    () => ({
      page: page + 1,
      limit,
      sort,
      search: debouncedSearch || undefined,
      itemId: item?.id || undefined,
      kind: kinds.length ? kinds.join(",") : undefined,
      startDate: dayStartISO(from),
      endDate: dayEndISO(to),
    }),
    [page, limit, sort, debouncedSearch, item, kinds, from, to]
  );

  const load = useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const { data: d } = await api.getStockLedger(params);
      if (id === reqId.current) setData(d);
    } catch (e) {
      if (id === reqId.current) openSnackbar(e?.response?.data?.message || "Failed to load ledger");
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [params, openSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change goes back to page 1.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, item, kinds, from, to, sort, limit]);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== "custom") {
      const r = presetRange(key);
      setFrom(r.from);
      setTo(r.to);
    }
  };
  const toggleKind = (k) => setKinds((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const clearAll = () => {
    setSearch("");
    setItem(null);
    setKinds([]);
    applyPreset("all");
  };
  const hasFilters = search || item || kinds.length || preset !== "all";
  const fmtDate = (d) => formatInTimezone(d, "DD MMM YYYY, hh:mm A");
  const showBalance = !!data.item;
  const totalAll = useMemo(
    () => Object.values(data.kindCounts || {}).reduce((a, b) => a + b, 0),
    [data.kindCounts]
  );

  const docCell = (r) =>
    r.doc ? (
      <>
        <div className={classes.strong}>
          {DOC_PREFIX[r.doc.type] ? `${DOC_PREFIX[r.doc.type]} ` : ""}
          #{r.doc.number}
          {r.doc.deleted && <span className={classes.deleted}>DELETED</span>}
        </div>
        {r.doc.party ? <div className={`${classes.muted} ${classes.ellipsis}`}>{r.doc.party}</div> : null}
      </>
    ) : (
      <span className={classes.muted}>{r.kind === "OPENING" ? "Opening stock" : "—"}</span>
    );

  const qtyCell = (r) => (
    <span className={r.effect >= 0 ? classes.plus : classes.minus}>
      {fmtSigned(r.effect)} <span className={classes.muted}>{r.unit}</span>
    </span>
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <h2 className={classes.title}>
            <SwapVertIcon className={classes.titleIcon} /> Item Transaction Ledger
          </h2>
          <p className={classes.subtitle}>Every stock movement — sales, purchases, adjustments, returns — with document numbers.</p>
        </div>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.panel}>
        <div className={classes.filterRow}>
          <TextField
            className={classes.grow}
            size="small"
            variant="outlined"
            placeholder="Search item, invoice / purchase no., customer, supplier, note…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" style={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null,
            }}
          />
          <Autocomplete
            className={classes.itemPicker}
            size="small"
            options={itemOptions}
            value={item}
            loading={itemLoading}
            onChange={(_, v) => setItem(v)}
            onInputChange={(_, v, reason) => reason === "input" && setItemQuery(v)}
            getOptionLabel={(o) => o.name || ""}
            getOptionSelected={(o, v) => o.id === v.id}
            filterOptions={(x) => x}
            ListboxProps={{ onScroll: onItemListScroll }}
            noOptionsText="No items"
            renderOption={(o) => (
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.82rem" }}>{o.name}</div>
                <div className={classes.muted}>{[o.category, o.barcode].filter(Boolean).join(" · ")}</div>
              </div>
            )}
            renderInput={(p) => (
              <TextField
                {...p}
                variant="outlined"
                placeholder="Filter by item (shows running balance)"
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {itemLoading ? <CircularProgress size={14} /> : null}
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <DateRangePicker
            from={from}
            to={to}
            preset={preset}
            onChange={(pr, f, t) => {
              setPreset(pr);
              setFrom(f);
              setTo(t);
            }}
          />
        </div>

        <div className={classes.presetRow}>
          <span
            className={`${classes.chip} ${kinds.length === 0 ? classes.chipActive : ""}`}
            onClick={() => setKinds([])}
          >
            All <span className={classes.chipCount}>{fmtQty(totalAll)}</span>
          </span>
          <span className={classes.sep} />
          {KIND_ORDER.filter((k) => data.kindCounts?.[k] || kinds.includes(k)).map((k) => {
            const m = KIND_META[k];
            const on = kinds.includes(k);
            return (
              <span
                key={k}
                className={`${classes.chip} ${on ? classes.chipActive : ""}`}
                style={on ? { borderColor: m.color, color: m.color, background: m.bg } : undefined}
                onClick={() => toggleKind(k)}
              >
                {m.label} <span className={classes.chipCount}>{fmtQty(data.kindCounts?.[k] || 0)}</span>
              </span>
            );
          })}
          {hasFilters && (
            <Button size="small" onClick={clearAll} style={{ textTransform: "none", fontSize: "0.72rem", minWidth: 0, marginLeft: "auto" }}>
              Clear all
            </Button>
          )}
          {data.item && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
              style={{ marginLeft: hasFilters ? 0 : "auto" }}
            >
              <ToggleButton value="timeline" style={{ textTransform: "none", padding: "1px 10px", fontSize: "0.72rem" }}>Timeline</ToggleButton>
              <ToggleButton value="table" style={{ textTransform: "none", padding: "1px 10px", fontSize: "0.72rem" }}>Table</ToggleButton>
            </ToggleButtonGroup>
          )}
        </div>
      </div>

      {data.item && <ItemHero item={data.item} symbol={symbol} />}

      <div className={classes.body}>
        {loading && (
          <div className={classes.overlay}>
            <CircularProgress size={26} style={{ color: "#f97316" }} />
          </div>
        )}
        <div className={classes.scroll}>
          {data.rows.length === 0 && !loading ? (
            <div className={classes.empty}>
              <SyncAltIcon style={{ fontSize: 40, color: "#cbd5e1" }} />
              <Typography style={{ fontWeight: 700, color: "#334155" }}>No transactions found</Typography>
              <Typography variant="caption">Try a wider date range or clear the filters.</Typography>
            </div>
          ) : data.item && view === "timeline" ? (
            <ItemTimeline
              rows={data.rows}
              symbol={symbol}
              fmtDay={(d) => formatInTimezone(d, "ddd, DD MMM YYYY")}
              fmtTime={(d) => formatInTimezone(d, "hh:mm A")}
              onSelect={setSelected}
            />
          ) : isMobile ? (
            <div className={classes.cards}>
              {data.rows.map((r) => (
                <div key={r.id} className={classes.card} onClick={() => setSelected(r)}>
                  <div className={classes.cardTop}>
                    <Kind kind={r.kind} classes={classes} />
                    <span className={classes.muted}>{fmtDate(r.date)}</span>
                  </div>
                  <div className={classes.cardMid}>
                    <span className={`${classes.strong} ${classes.ellipsis}`} style={{ maxWidth: "62%" }}>{r.item.name}</span>
                    <span style={{ fontSize: "0.92rem" }}>{qtyCell(r)}</span>
                  </div>
                  <div className={classes.cardBot}>
                    <span style={{ fontSize: "0.72rem" }}>{docCell(r)}</span>
                    <span className={classes.muted}>
                      {r.line ? `${fmtQty(r.line.qty)} × ${fmtMoney(r.line.rate, symbol)} = ${fmtMoney(r.line.amount, symbol)}` : ""}
                      {showBalance && r.balance != null ? ` · Bal ${fmtQty(r.balance)}` : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className={classes.table}>
              <thead>
                <tr>
                  <th
                    className={classes.th}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSort((s) => (s === "desc" ? "asc" : "desc"))}
                  >
                    Date{" "}
                    {sort === "desc" ? (
                      <ArrowDownwardIcon style={{ fontSize: 11, verticalAlign: "middle" }} />
                    ) : (
                      <ArrowUpwardIcon style={{ fontSize: 11, verticalAlign: "middle" }} />
                    )}
                  </th>
                  <th className={classes.th}>Type</th>
                  <th className={classes.th}>Item</th>
                  <th className={classes.th}>Document</th>
                  <th className={`${classes.th} ${classes.right}`}>Qty</th>
                  <th className={`${classes.th} ${classes.right}`}>Rate</th>
                  <th className={`${classes.th} ${classes.right}`}>Amount</th>
                  {showBalance && <th className={`${classes.th} ${classes.right}`}>Balance</th>}
                  <th className={classes.th}>By</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className={classes.tr} onClick={() => setSelected(r)}>
                    <td className={classes.td} style={{ whiteSpace: "nowrap" }}>{fmtDate(r.date)}</td>
                    <td className={classes.td}><Kind kind={r.kind} classes={classes} /></td>
                    <td className={classes.td}>
                      <div className={`${classes.strong} ${classes.ellipsis}`}>{r.item.name}</div>
                      {r.item.category ? <div className={classes.muted}>{r.item.category}</div> : null}
                      {r.batch ? (
                        <div className={classes.muted}>
                          Batch {r.batch.batchNo}
                          {r.batch.expiryDate ? ` · exp ${formatInTimezone(r.batch.expiryDate, "DD MMM YYYY")}` : ""}
                        </div>
                      ) : null}
                    </td>
                    <td className={classes.td}>{docCell(r)}</td>
                    <td className={`${classes.td} ${classes.right}`} style={{ whiteSpace: "nowrap" }}>{qtyCell(r)}</td>
                    <td className={`${classes.td} ${classes.right}`}>{r.line ? fmtMoney(r.line.rate, symbol) : "-"}</td>
                    <td className={`${classes.td} ${classes.right} ${classes.strong}`}>{r.line ? fmtMoney(r.line.amount, symbol) : "-"}</td>
                    {showBalance && (
                      <td className={`${classes.td} ${classes.right} ${classes.strong}`}>
                        {r.balance != null ? fmtQty(r.balance) : "-"}
                      </td>
                    )}
                    <td className={classes.td}><span className={classes.ellipsis} style={{ display: "block", maxWidth: 110 }}>{r.user || "-"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className={classes.footer}>
          <TablePagination
            component="div"
            count={data.total || 0}
            page={page}
            onChangePage={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onChangeRowsPerPage={(e) => setLimit(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage={isMobile ? "Rows" : "Rows per page"}
          />
        </div>
      </div>

      <Drawer anchor="right" open={!!selected} onClose={() => setSelected(null)} classes={{ paper: classes.drawer }}>
        {selected && (
          <>
            <div className={classes.dHead}>
              <div>
                <Kind kind={selected.kind} classes={classes} />
                <Typography style={{ fontWeight: 800, fontSize: "1rem", marginTop: 4 }}>{selected.item.name}</Typography>
                <Typography className={classes.muted}>{fmtDate(selected.date)}</Typography>
              </div>
              <IconButton size="small" onClick={() => setSelected(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            <div className={classes.dBody}>
              <div className={classes.dSection}>
                <div className={classes.dTitle}>Stock movement</div>
                <DRow classes={classes} k="Change" v={<span className={selected.effect >= 0 ? classes.plus : classes.minus}>{fmtSigned(selected.effect)} {selected.unit}</span>} />
                {selected.stockUnit && selected.stockUnit !== selected.unit && (
                  <DRow classes={classes} k={`In stock unit (${selected.stockUnit})`} v={fmtSigned(selected.effectStock)} />
                )}
                <DRow classes={classes} k="Balance after" v={selected.balance != null ? `${fmtQty(selected.balance)} ${selected.stockUnit}` : "Select an item to see balance"} />
                <DRow classes={classes} k="Ledger type" v={selected.transactionType} />
                <DRow classes={classes} k="Category" v={selected.item.category} />
                <DRow classes={classes} k="Barcode" v={selected.item.barcode} />
              </div>

              {selected.line && (
                <div className={classes.dSection}>
                  <div className={classes.dTitle}>Line details</div>
                  <DRow classes={classes} k="Quantity" v={fmtQty(selected.line.qty)} />
                  <DRow classes={classes} k={selected.kind === "PURCHASE" || selected.kind.startsWith("ADJUST") ? "Cost" : "Rate"} v={fmtMoney(selected.line.rate, symbol)} />
                  <DRow classes={classes} k="Line amount" v={fmtMoney(selected.line.amount, symbol)} />
                </div>
              )}

              {selected.doc && (
                <div className={classes.dSection}>
                  <div className={classes.dTitle}>Source document</div>
                  <DRow classes={classes} k="Type" v={DOC_TYPE_LABEL[selected.doc.type] || "-"} />
                  <DRow classes={classes} k="Number" v={`#${selected.doc.number}`} />
                  <DRow classes={classes} k={selected.doc.type === "purchase" ? "Supplier" : "Customer"} v={selected.doc.party} />
                  <DRow classes={classes} k="Document total" v={selected.doc.total != null ? fmtMoney(selected.doc.total, symbol) : null} />
                  <DRow classes={classes} k="Items in document" v={selected.doc.lineCount} />
                  <DRow classes={classes} k="Status" v={selected.doc.status} />
                  <DRow classes={classes} k="Payment mode" v={selected.doc.paymentMode} />
                  <DRow classes={classes} k="Adjustment type" v={selected.doc.adjustType} />
                  <DRow classes={classes} k="Reason" v={selected.doc.reason} />
                  <DRow classes={classes} k="Document date" v={selected.doc.docDate ? fmtDate(selected.doc.docDate) : null} />
                  {selected.doc.deleted && <DRow classes={classes} k="Deleted" v={selected.doc.deleteReason || "Yes"} />}
                  {selected.doc.missing && <DRow classes={classes} k="Note" v="Source document no longer exists" />}
                  {!inSuperAdmin && selected.doc.id && DOC_PATH[selected.doc.type] && !selected.doc.missing && (
                    <Button
                      component={RouterLink}
                      to={`${DOC_PATH[selected.doc.type]}${selected.doc.id}`}
                      size="small"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      style={{ marginTop: 8, textTransform: "none" }}
                    >
                      Open {DOC_TYPE_LABEL[selected.doc.type]}
                    </Button>
                  )}
                </div>
              )}

              <div className={classes.dSection}>
                <div className={classes.dTitle}>Audit</div>
                <DRow classes={classes} k="Reference" v={selected.note} />
                {selected.batch && (
                  <DRow
                    classes={classes}
                    k="Batch"
                    v={`${selected.batch.batchNo}${
                      selected.batch.expiryDate ? ` (expires ${formatInTimezone(selected.batch.expiryDate, "DD MMM YYYY")})` : ""
                    }`}
                  />
                )}
                <DRow classes={classes} k="Recorded by" v={selected.user} />
                <DRow classes={classes} k="Ledger ID" v={<span style={{ fontFamily: "monospace", fontSize: "0.7rem" }}>{selected.id}</span>} />
              </div>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
}
